import { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  Flex,
  TextInput,
  Textarea,
  createForm,
  useId,
  useToasts,
} from '@applyboard/crystal-ui';
import { useEventTracker } from '@applyboard/ui-utils';
import { ContactVasSupportType, useContactVasSupport } from '../../data/contactVasSupport';
import { ContactVasSupport } from './types';

type ContactVasSupportDialogProps = {
  name: string;
  email: string;
  service: string;
  vendor: string;
  buttonSize?: 'sm' | 'md' | 'lg';
  buttonWidth?: 'hug' | 'fill';
};

const { Field, Form } = createForm<ContactVasSupport>();

export function ContactVasSupportDialog({
  name,
  email,
  service,
  vendor,
  buttonSize = 'md',
  buttonWidth = 'hug',
}: ContactVasSupportDialogProps) {
  const defaultFormValues: ContactVasSupport = {
    name,
    email,
    inquiryDetails: '',
  };
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(false);
  const { trackEvent } = useEventTracker();
  const toasts = useToasts();
  const { contactVasSupport } = useContactVasSupport();

  const submitCallback = useCallback(
    (values: ContactVasSupport) => {
      trackEvent({
        event: 'VAS - Vas Hub Page - Contact Vas support submitted',
        data: { service, vendor },
      });

      setSubmitDisabled(true);

      const contactVasSupportPayload: ContactVasSupportType = {
        name: values.name,
        email: values.email,
        inquiry_details: values.inquiryDetails,
      };

      contactVasSupport(
        contactVasSupportPayload,
        () => {
          setSubmitDisabled(false);
          trackEvent({
            event: 'VAS - Vas Hub Page - Vas Contact Support Submission Completed',
            data: { service, vendor, inquiry: values.inquiryDetails },
          });
          toasts.positive('Your request has been submitted. Our team will contact you shortly.');
          setOpen(false);
        },
        error => {
          console.error('Failed to submit contact vas support request', error);
          toasts.negative(
            new Error(
              'We couldn’t send your message. Please try again later or email loan.support@applyboard.com directly.',
            ),
          );
          setSubmitDisabled(false);
        },
      );
    },
    [trackEvent, service, vendor, contactVasSupport, toasts],
  );

  return (
    <Dialog open={open} onOpenChange={(state: boolean) => setOpen(state)} size="md">
      <Dialog.Trigger>
        <Button
          aria-label="messages"
          size={buttonSize}
          intent="primary"
          emphasis="contained"
          width={buttonWidth}
          disabled={submitDisabled}
          onClick={() => {
            trackEvent({
              event: 'VAS - Vas Hub Page -  Vas Contact Support button clicked',
              data: { service, vendor },
            });
          }}
        >
          Contact Support
        </Button>
      </Dialog.Trigger>
      <Dialog.Heading>Contact Support Loan Support</Dialog.Heading>
      <Dialog.Content>
        <Form id={formId} defaultValues={defaultFormValues} onSubmit={submitCallback}>
          <Flex align="stretch" direction="column" gap={{ lg: 8, sm: 4, xs: 2 }}>
            <Field as={TextInput} name="name" label="Name" required="Your name is required" />
            <Field as={TextInput} name="email" label="Email" required="Email is required" />
            <Field
              as={Textarea}
              name="inquiryDetails"
              label="Inquiry Details"
              required="Inquiry details is required"
            />
          </Flex>
        </Form>
      </Dialog.Content>
      <Dialog.Actions>
        <Dialog.CloseButton width="fill">Cancel</Dialog.CloseButton>
        <Button intent="primary" width="fill" type="submit" form={formId} disabled={submitDisabled}>
          Submit
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
}
